import { useCallback, useEffect, useRef, useState } from "react";
import { RiArrowDownSLine } from "react-icons/ri";
import VATChart from "./Charts/VATChart";
import ProfitChart from "./Charts/ProfitChart";
import BottlesChart from "./Charts/BottlesChart";
import axios from "axios";
import { useParams } from "react-router-dom";
import { DatePicker, Space } from "antd";
const { RangePicker } = DatePicker;
import dayjs from "dayjs";

import { motion, AnimatePresence } from "framer-motion";
import {
  itemVariants,
  inputAnimations,
  dropdownVariants,
} from "../../../utils/Animations";

const ChartDashboard = () => {
  const apiUrl = import.meta.env.VITE_METERI_API_BASE_URL;
  const token = localStorage.getItem("token");
  const { rest_slug2 } = useParams();

  // Chart and range options
  const chartOptions = [
    "Grafico a Barre",
    // 'Grafico Lineare',
    "Grafico a Torta",
    "Numerico",
  ];

  const rangeOptions = [
    { label: "Ultimi 7 giorni", value: "7d" },
    { label: "Ultimi 30 giorni", value: "30d" },
    { label: "Ultimi 3 mesi", value: "3m" },
    { label: "Ultimi 6 mesi", value: "6m" },
    { label: "Personalizzato", value: "custom" },
  ];

  // State declarations
  const [selectedChart, setSelectedChart] = useState("Grafico a Barre");
  const [showChartDropdown, setShowChartDropdown] = useState(false);
  const [kpiData, setKpiData] = useState();
  const [showRangeDropdown, setShowRangeDropdown] = useState(false);
  const [selectedRange, setSelectedRange] = useState(rangeOptions[1]);
  const [range, setRange] = useState(false);
  const [startDate, setStartDate] = useState(null);
  const [endDate, setEndDate] = useState(null);

  const datePickerRef = useRef(null);
  const rangeDropdownRef = useRef(null);

  // Data fetching function
  const DashboardKpiData = useCallback(async () => {
    try {
      const params = !range
        ? {
          range: selectedRange?.value,
          restaurant_id: rest_slug2,
        }
        : {
          range: "custom",
          start_date: startDate,
          end_date: endDate,
          restaurant_id: rest_slug2,
        };

      const response = await axios.get(
        `${apiUrl}/api/v1/revenue-by-wine-type`,
        {
          params,
          headers: {
            Authorization: `Bearer ${token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );

      setKpiData(response.data);
    } catch (error) {
      console.error("Error fetching KPI data:", error);
    }
  }, [apiUrl, endDate, range, rest_slug2, selectedRange, startDate, token]);

  // Helper functions for data transformation
  const getDynamicChartData = (kpiData) => {
    if (!kpiData) return [];

    return Object.entries(kpiData).map(([name, value]) => ({
      name,
      value: parseFloat(value),
      fill: getColorForWineType(name),
    }));
  };

  const getColorForWineType = (name) => {
    const colorMap = {
      "Vini Bianchi": "#4CAF50",
      "Vini Veneti": "#aaaaaa",
      "Vini Liguri": "#8a2432",
      "Vini del Rodano": "#FFBB28",
    };
    return colorMap[name] || "#888888";
  };

  const getVATServingMethodData = (kpiData) => {
    if (!kpiData) return [];

    return [
      {
        name: "Bottiglia",
        value: parseFloat(kpiData.total_bottle_revenue || 0),
        fill: "#d26574",
      },
      {
        name: "Calice",
        value: parseFloat(kpiData.total_glass_revenue || 0),
        fill: "#07ED9D",
      },
    ];
  };

  const getBottleServingMethodData = (kpiData) => {
    if (!kpiData) return [];

    return [
      {
        name: "Bottiglia",
        value: parseFloat(kpiData.total_bottle_quantity || 0),
        fill: "#d26574",
      },
      {
        name: "Calice",
        value: parseFloat(kpiData.total_glass_quantity || 0),
        fill: "#07ED9D",
      },
    ];
  };

  // Chart data preparation
  const VATChartData = getDynamicChartData(kpiData?.data?.revenue_by_wine_type);
  const VATChartServingMethodData = getVATServingMethodData(kpiData?.data);
  const BottlesChartData = getDynamicChartData(
    kpiData?.data?.quantity_by_wine_type
  );
  const BottlesChartServingMethodData = getBottleServingMethodData(
    kpiData?.data
  );
  const ProfitChartData = getDynamicChartData(
    kpiData?.data?.profit_margin_by_wine_type
  );

  // Date range presets
  const rangePresets = [
    // Custom (Special Case)
    // { label: "Custom Range", value: null, isCustom: true },

    // Quick Ranges
    { label: "Oggi", value: [dayjs().startOf("day"), dayjs().endOf("day")] },
    {
      label: "Ieri",
      value: [
        dayjs().subtract(1, "day").startOf("day"),
        dayjs().subtract(1, "day").endOf("day"),
      ],
    },

    // Last N Days
    { label: "Ultimi 7 Giorni", value: [dayjs().subtract(7, "day"), dayjs()] },
    { label: "Ultimi 14 Giorni", value: [dayjs().subtract(14, "day"), dayjs()] },
    { label: "Ultimi 28 Giorni", value: [dayjs().subtract(28, "day"), dayjs()] },
    { label: "Ultimi 30 Giorni", value: [dayjs().subtract(30, "day"), dayjs()] },
    { label: "Ultimi 90 Giorni", value: [dayjs().subtract(90, "day"), dayjs()] },

    // Week-Based
    {
      label: "Questa Settimana",
      value: [dayjs().startOf("week"), dayjs().endOf("day")],
    },
    {
      label: "Settimana Scorsa",
      value: [
        dayjs().subtract(1, "week").startOf("week"),
        dayjs().subtract(1, "week").endOf("week"),
      ],
    },

    // Month/Year
    {
      label: "Questo Mese",
      value: [dayjs().startOf("month"), dayjs().endOf("day")],
    },
    {
      label: "Mese Scorso",
      value: [
        dayjs().subtract(1, "month").startOf("month"),
        dayjs().subtract(1, "month").endOf("month"),
      ],
    },
    {
      label: "Ultimi 12 Mesi",
      value: [dayjs().subtract(12, "month"), dayjs()],
    },

    // Yearly
    {
      label: "Quest'anno",
      value: [dayjs().startOf("year"), dayjs().endOf("day")],
    },
    {
      label: "Ultimo Anno Solare",
      value: [
        dayjs().subtract(1, "year").startOf("year"),
        dayjs().subtract(1, "year").endOf("year"),
      ],
    },
  ];

  // Event handlers
  const onRangeChange = (dates) => {
    if (dates && dates[0] && dates[1]) {
      // When valid dates are selected
      const start = dates[0].format("YYYY-MM-DD");
      const end = dates[1].format("YYYY-MM-DD");

      setStartDate(start);
      setEndDate(end);
      setRange(true);

      // Set to custom range when dates are selected
      setSelectedRange(rangeOptions.find(opt => opt.value === "custom"));
    } else {
      // When cleared (dates are null or undefined)
      setStartDate(null);
      setEndDate(null);
      setRange(false);

      // Optionally reset to default range or keep empty
      // If you want to reset to default range (Last 7 Days) when cleared:
      const defaultStart = dayjs().subtract(7, "day").format("YYYY-MM-DD");
      const defaultEnd = dayjs().format("YYYY-MM-DD");

      setStartDate(defaultStart);
      setEndDate(defaultEnd);
      setRange(true);

      // Or if you want to keep them empty:
      // setStartDate('');
      // setEndDate('');
      // setRange(false);
    }
  };

  // Effects
  useEffect(() => {
    DashboardKpiData();
  }, [startDate, endDate, selectedRange, range, DashboardKpiData]);

  useEffect(() => {
    if (selectedRange?.value !== "custom") {
      setStartDate(null);
      setEndDate(null);
      setRange(false);
    }
  }, [selectedRange]);

  // Close dropdowns when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      // Close chart dropdown
      if (!event.target.closest(".chart-dropdown-container")) {
        setShowChartDropdown(false);
      }

      // Close range dropdown
      if (
        rangeDropdownRef.current &&
        !rangeDropdownRef.current.contains(event.target)
      ) {
        setShowRangeDropdown(false);
      }
    };

    document.addEventListener("mousedown", handleClickOutside);
    return () => {
      document.removeEventListener("mousedown", handleClickOutside);
    };
  }, []);

  return (
    <>
      <div className="w-full flex flex-col xl:flex-row gap-4 mb-5 justify-between">
        {/* Chart Type Dropdown */}
        <motion.div className="relative w-full lg:w-[300px] chart-dropdown-container" variants={itemVariants}>
          <motion.button
            onClick={() => {
              setShowChartDropdown(!showChartDropdown);
              setShowRangeDropdown(false);
            }}
            {...inputAnimations}
            className={`py-2 px-4 w-full border text-left bg-white border-[#F2C3CA] text-[#8B2433] font-medium rounded-full flex items-center justify-between shadow-sm poppins-regular`}
          >
            {selectedChart}
            <RiArrowDownSLine
              className={`transform transition-transform duration-300 ${showChartDropdown ? "rotate-180" : ""
                }`}
              size={24}
              color="#9f414f"
            />
          </motion.button>
          <AnimatePresence>
            {showChartDropdown && (
              <motion.div className="absolute mt-1 w-full border border-[#F2C3CA] bg-white rounded-2xl z-50 shadow-md "
                variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit">
                {chartOptions
                  .filter((option) => option !== selectedChart)
                  .map((option, idx) => (
                    <motion.div
                      variants={itemVariants}

                      key={idx}
                      className="px-4 py-2 hover:bg-[#f6efef] cursor-pointer text-[#8B2433] poppins-regular"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedChart(option);
                        setShowChartDropdown(false);
                      }}

                    >
                      {option}
                    </motion.div>
                  ))}
              </motion.div>
            )}
          </AnimatePresence>
        </motion.div>

        <motion.div className="range-wrapper flex flex-col lg:flex-row gap-4" variants={itemVariants}>
          {/* Date Range Picker Container */}
          {selectedRange.value === "custom" && (
            <div className="relative w-full lg:w-[200px] xl:w-[230px]" ref={datePickerRef}>
              <div className="w-full">
                <Space direction="vertical" className="w-full">
                  <RangePicker
                    className="custom-range-picker w-full no-hover-range"
                    onChange={onRangeChange}
                    format="YYYY/MM/DD"
                    popupClassName="custom-range-picker-dropdown"
                    presets={rangePresets}
                    separator={<span className="text-[#8B2433]">-</span>}
                    disabledDate={(current) => current && current > dayjs().endOf('day')}
                    allowClear={true}
                    // Add these props to show March and April by default
                    defaultPickerValue={[dayjs().subtract(1, 'month'), dayjs()]}
                    placeholder={['Inizio', 'Fine']}
                  />
                </Space>
              </div>
            </div>
          )}

          {/* Time Range Dropdown */}
          <motion.div className="relative w-full lg:w-[300px]" ref={rangeDropdownRef} variants={itemVariants}>
            <motion.button
              onClick={() => {
                setShowRangeDropdown(!showRangeDropdown);
                setShowChartDropdown(false);
                if (selectedRange?.value === "custom") {
                  setRange(true);
                } else {
                  setRange(false);
                }
              }}
              {...inputAnimations}
              className={`py-2 px-4 w-full border text-left bg-white border-[#F2C3CA] text-[#8B2433] font-medium rounded-full flex items-center justify-between shadow-sm poppins-regular`}
            >
              {selectedRange.label || "Seleziona Intervallo"}
              <RiArrowDownSLine
                className={`transform transition-transform duration-300 ${showRangeDropdown ? "rotate-180" : ""
                  }`}
                size={24}
                color="#9f414f"
              />
            </motion.button>
            <AnimatePresence>
              {showRangeDropdown && (
                <motion.div className="absolute mt-1 w-full border border-[#F2C3CA] bg-white rounded-2xl z-50 shadow-md overflow-hidden"
                 variants={dropdownVariants}
                initial="hidden"
                animate="visible"
                exit="exit">
                  {rangeOptions.map((option, idx) => (
                    <div
                      key={idx}
                      className="px-4 py-2 hover:bg-[#f6efef] cursor-pointer text-[#8B2433] poppins-regular"
                      onClick={(e) => {
                        e.stopPropagation();
                        setSelectedRange(option);
                        setShowRangeDropdown(false);
                      }}
                    >
                      {option.label}
                    </div>
                  ))}
                </motion.div>
              )}
            </AnimatePresence>
          </motion.div>
        </motion.div>

      </div>
      <div className="space-y-10">
        <VATChart
          chartType={selectedChart}
          wineTypeData={VATChartData}
          servingMethodData={VATChartServingMethodData}
        />
        <BottlesChart
          chartType={selectedChart}
          wineTypeData={BottlesChartData}
          servingMethodData={BottlesChartServingMethodData}
        />
        <ProfitChart chartType={selectedChart} data={ProfitChartData} />
      </div>
    </>
  );
};

export default ChartDashboard;